import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from "@mui/material";
import { Simulation, SimulationMimshakData } from "../../../interfaces/DTO/simulation";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { Contact } from "../../../interfaces/DTO/crm/contact";
import { formatHandlerCode, formatPercentage } from "../../../utils/namesFormater";
import { formatProductType } from "../../../utils/enumFormatter";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface Props {
  open: boolean;
  simulation: Simulation;
  simulationData: SimulationMimshakData[]; // ✅ מקבל את הנתונים המסוננים
  currentData: MimshakData[]; // ✅ מקבל את הנתונים הנוכחיים
  contacts: Contact[];
  onClose: () => void;
}

interface PolicyComparison {
  policy_number: string;
  product_type: number;
  contact_id: number;
  simData: SimulationMimshakData;
  currentData?: MimshakData;
  status: "completed" | "pending" | "modified" | "unplanned";
  handlerMatch: boolean;
  feeMatch: boolean;
  details: string;
}

const SimulationComparisonDialog: React.FC<Props> = ({
  open,
  simulation,
  simulationData,
  currentData,
  contacts,
  onClose,
}) => {
  const contactMap = useMemo(() => {
    return contacts.reduce<Record<number, string>>((acc, contact) => {
      acc[contact.id] = `${contact.first_name || ""} ${contact.last_name || ""}`.trim();
      return acc;
    }, {});
  }, [contacts]);

  const comparison = useMemo(() => {
    const currentMap = new Map(currentData.map((r) => [r.policy_number, r]));
    
    const policies: PolicyComparison[] = simulationData.map((simRow) => {
      const currentRow = currentMap.get(simRow.policy_number);
      
      const handlerMatch = currentRow?.handler_code === simRow.handler_code;
      const feeMatch = currentRow?.saving_fee === simRow.saving_fee;
      
      let status: PolicyComparison["status"] = "pending";
      let details = "";

      if (!currentRow) {
        status = "pending";
        details = "הפוליסה עדיין לא עודכנה";
      } else if (handlerMatch && feeMatch) {
        status = "completed";
        details = "בוצע כמתוכנן";
      } else if (!handlerMatch || !feeMatch) {
        status = "modified";
        const changes = [];
        if (!handlerMatch) {
          changes.push(`יצרן: ${formatHandlerCode(simRow.handler_code)} → ${formatHandlerCode(currentRow.handler_code)}`);
        }
        if (!feeMatch) {
          changes.push(`דמ"נ: ${formatPercentage(simRow.saving_fee)} → ${formatPercentage(currentRow.saving_fee)}`);
        }
        details = changes.join(", ");
      }

      return {
        policy_number: simRow.policy_number,
        product_type: simRow.product_type,
        contact_id: simRow.contact_id,
        simData: simRow,
        currentData: currentRow,
        status,
        handlerMatch,
        feeMatch,
        details,
      };
    });

    // שינויים לא מתוכננים - פוליסות שהשתנו אבל לא היו בסימולציה
    const simPolicyNumbers = new Set(simulationData.map((s) => s.policy_number));
    const unplannedChanges: PolicyComparison[] = [];

    currentData.forEach((current) => {
      if (simPolicyNumbers.has(current.policy_number)) return;
      
      // בדוק אם יש לפוליסה זו original data ואם היה שינוי
      const originalInSim = simulationData.find(s => 
        s.contact_id === current.contact_id && 
        s.product_type === current.product_type
      );
      
      if (originalInSim && originalInSim.handler_code !== current.handler_code) {
        unplannedChanges.push({
          policy_number: current.policy_number,
          product_type: current.product_type,
          contact_id: current.contact_id,
          simData: current as SimulationMimshakData,
          currentData: current,
          status: "unplanned",
          handlerMatch: false,
          feeMatch: false,
          details: `שינוי לא מתוכנן: ${formatHandlerCode(current.handler_code)}`,
        });
      }
    });

    const completed = policies.filter((p) => p.status === "completed");
    const pending = policies.filter((p) => p.status === "pending");
    const modified = policies.filter((p) => p.status === "modified");

    return {
      completed,
      pending,
      modified,
      unplanned: unplannedChanges,
      total: policies.length,
      completionRate: policies.length > 0 ? (completed.length / policies.length) * 100 : 0,
    };
  }, [simulationData, currentData]);

  const StatusCard: React.FC<{
    title: string;
    count: number;
    color: string;
    icon: React.ReactNode;
    policies: PolicyComparison[];
  }> = ({ title, count, color, icon, policies }) => (
    <Card sx={{ border: `2px solid ${color}` }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Box sx={{ color }}>{icon}</Box>
          <Box flex={1}>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="h4" sx={{ color }}>
              {count}
            </Typography>
          </Box>
        </Stack>
        
        {policies.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">הצג פרטים</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {policies.map((policy) => (
                  <Card
                    key={policy.policy_number}
                    variant="outlined"
                    sx={{
                      bgcolor: "grey.50",
                      borderLeft: `4px solid ${color}`,
                    }}
                  >
                    <CardContent>
                      {/* כותרת הפוליסה */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          פוליסה {policy.policy_number}
                        </Typography>
                        <Chip 
                          label={formatProductType(policy.product_type)} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </Stack>

                      <Typography variant="body2" color="text.secondary" mb={2}>
                        לקוח: {contactMap[policy.contact_id]}
                      </Typography>

                      <Divider sx={{ my: 1 }} />

                      {/* פרטי השינוי המתוכנן */}
                      <Box sx={{ bgcolor: "info.50", p: 1.5, borderRadius: 1, mb: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold" color="info.main" gutterBottom>
                          📋 מה תוכנן בסימולציה:
                        </Typography>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">
                            • יצרן: <strong>{formatHandlerCode(policy.simData.handler_code)}</strong>
                          </Typography>
                          <Typography variant="body2">
                            • דמי ניהול: <strong>{formatPercentage(policy.simData.saving_fee)}</strong>
                          </Typography>
                          <Typography variant="body2">
                            • סכום: <strong>{policy.simData.funds_sum.toLocaleString("he-IL")} ₪</strong>
                          </Typography>
                        </Stack>
                      </Box>

                      {/* מה קרה בפועל */}
                      {policy.status === "completed" && (
                        <Box sx={{ bgcolor: "success.50", p: 1.5, borderRadius: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="success.main" gutterBottom>
                            ✅ מה בוצע:
                          </Typography>
                          <Typography variant="body2">
                            השינוי בוצע בדיוק כמתוכנן - כל הפרמטרים תואמים
                          </Typography>
                        </Box>
                      )}

                      {policy.status === "pending" && (
                        <Box sx={{ bgcolor: "warning.50", p: 1.5, borderRadius: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="warning.main" gutterBottom>
                            ⏳ סטטוס נוכחי:
                          </Typography>
                          <Typography variant="body2">
                            הפוליסה עדיין לא עודכנה - השינוי ממתין לביצוע
                          </Typography>
                        </Box>
                      )}

                      {policy.status === "modified" && policy.currentData && (
                        <Box sx={{ bgcolor: "error.50", p: 1.5, borderRadius: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="error.main" gutterBottom>
                            ⚠️ מה בוצע בפועל (שונה מהתוכנית):
                          </Typography>
                          <Stack spacing={0.5}>
                            {!policy.handlerMatch && (
                              <Typography variant="body2">
                                • יצרן: <strong>{formatHandlerCode(policy.currentData.handler_code)}</strong>
                                <Typography component="span" color="error" fontSize="0.85rem">
                                  {" "}(תוכנן: {formatHandlerCode(policy.simData.handler_code)})
                                </Typography>
                              </Typography>
                            )}
                            {!policy.feeMatch && (
                              <Typography variant="body2">
                                • דמי ניהול: <strong>{formatPercentage(policy.currentData.saving_fee)}</strong>
                                <Typography component="span" color="error" fontSize="0.85rem">
                                  {" "}(תוכנן: {formatPercentage(policy.simData.saving_fee)})
                                </Typography>
                              </Typography>
                            )}
                            {policy.currentData.funds_sum !== policy.simData.funds_sum && (
                              <Typography variant="body2">
                                • סכום: <strong>{policy.currentData.funds_sum.toLocaleString("he-IL")} ₪</strong>
                                <Typography component="span" color="info.main" fontSize="0.85rem">
                                  {" "}(היה: {policy.simData.funds_sum.toLocaleString("he-IL")} ₪)
                                </Typography>
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      )}

                      {policy.status === "unplanned" && policy.currentData && (
                        <Box sx={{ bgcolor: "secondary.50", p: 1.5, borderRadius: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="secondary.main" gutterBottom>
                            ❌ שינוי לא מתוכנן:
                          </Typography>
                          <Typography variant="body2">
                            הפוליסה לא הייתה כלולה בסימולציה, אך בוצע שינוי:
                          </Typography>
                          <Typography variant="body2" mt={0.5}>
                            • יצרן נוכחי: <strong>{formatHandlerCode(policy.currentData.handler_code)}</strong>
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth dir="rtl">
      <DialogTitle>
        מעקב ביצוע סימולציה: {simulation.name}
        <Typography variant="body2" color="text.secondary">
          תקופה: {simulation.reported_period}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box>
          {/* מד התקדמות */}
          <Card sx={{ mb: 3, bgcolor: "primary.50" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  אחוז השלמה
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box flex={1}>
                    <LinearProgress
                      variant="determinate"
                      value={comparison.completionRate}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                  <Typography variant="h5" fontWeight="bold">
                    {comparison.completionRate.toFixed(0)}%
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {comparison.completed.length} מתוך {comparison.total} פוליסות בוצעו כמתוכנן
                </Typography>
              </CardContent>
            </Card>

            {/* כרטיסי סטטוס */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <StatusCard
                  title="בוצע כמתוכנן"
                  count={comparison.completed.length}
                  color="#4caf50"
                  icon={<CheckCircleIcon fontSize="large" />}
                  policies={comparison.completed}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <StatusCard
                  title="ממתין לביצוע"
                  count={comparison.pending.length}
                  color="#ff9800"
                  icon={<HourglassEmptyIcon fontSize="large" />}
                  policies={comparison.pending}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <StatusCard
                  title="שונה בניגוד לתוכנית"
                  count={comparison.modified.length}
                  color="#f44336"
                  icon={<WarningIcon fontSize="large" />}
                  policies={comparison.modified}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <StatusCard
                  title="שינויים לא מתוכננים"
                  count={comparison.unplanned.length}
                  color="#9c27b0"
                  icon={<ErrorIcon fontSize="large" />}
                  policies={comparison.unplanned}
                />
              </Grid>
            </Grid>
          </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          סגור
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimulationComparisonDialog;